import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Alert,
  AlertDescription,
} from './ui/alert';
import { 
  Upload, 
  FileText, 
  Image, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Sparkles,
  X,
  User,
  Phone,
  MapPin,
  Stethoscope,
  Edit,
  Table
} from 'lucide-react';
import { toast } from 'sonner';

const ImportarComIA = ({ onImportComplete }) => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'image/jpeg', 
      'image/png', 
      'image/webp'
    ];
    const validExtensions = ['.pdf', '.docx', '.xlsx', '.xls', '.csv', '.jpg', '.jpeg', '.png', '.webp'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      toast.error('Tipo de arquivo não suportado. Use PDF, DOCX, Excel, CSV, JPG, PNG ou WEBP.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    setSelectedFile(file);
    setExtractedData(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/import/extract-from-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setExtractedData(response.data);
      
      if (response.data.pacientes?.length === 0) {
        toast.warning('Nenhum paciente encontrado no documento');
      } else {
        toast.success(`${response.data.pacientes?.length} paciente(s) encontrado(s)`);
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Erro ao processar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmAll = async () => {
    if (!extractedData?.pacientes?.length) return;

    setConfirming(true);
    try {
      const response = await api.post('/import/confirm-patients', extractedData.pacientes);
      
      if (response.data.criados > 0) {
        toast.success(response.data.mensagem);
        setModalOpen(false);
        setExtractedData(null);
        setSelectedFile(null);
        onImportComplete?.(response.data.pacientes_criados);
      }
      
      if (response.data.erros > 0) {
        response.data.detalhes_erros.forEach(err => {
          toast.error(`Paciente ${err.indice + 1}: ${err.erro}`);
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar importação');
    } finally {
      setConfirming(false);
    }
  };

  const handleEditPatient = (index) => {
    setEditingIndex(index);
    setEditForm({ ...extractedData.pacientes[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null) {
      const newPacientes = [...extractedData.pacientes];
      newPacientes[editingIndex] = editForm;
      setExtractedData({ ...extractedData, pacientes: newPacientes });
      setEditingIndex(null);
      setEditForm({});
    }
  };

  const handleRemovePatient = (index) => {
    const newPacientes = extractedData.pacientes.filter((_, i) => i !== index);
    setExtractedData({ ...extractedData, pacientes: newPacientes });
  };

  const getConfidenceColor = (confianca) => {
    if (confianca >= 0.8) return 'text-green-600 bg-green-50';
    if (confianca >= 0.5) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getFileIcon = () => {
    if (!selectedFile) return <Upload className="h-8 w-8" />;
    if (selectedFile.type.includes('image')) return <Image className="h-8 w-8" />;
    if (selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) return <Table className="h-8 w-8" />;
    return <FileText className="h-8 w-8" />;
  };

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        onClick={() => setModalOpen(true)}
        data-testid="btn-importar-ia"
      >
        <Sparkles className="h-4 w-4" />
        Importar com IA (PDF/Foto/Word)
      </Button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Importar Pacientes com IA
            </DialogTitle>
            <DialogDescription>
              Faça upload de um documento (PDF, DOCX) ou foto para extrair dados de pacientes automaticamente
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            {/* File Upload Area */}
            {!extractedData && (
              <div className="space-y-4">
                <div
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
                    transition-colors duration-200
                    ${uploading ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="h-10 w-10 animate-spin text-primary" />
                      <p className="text-sm font-medium">Processando documento com IA...</p>
                      <p className="text-xs text-muted-foreground">Isso pode levar alguns segundos</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      {getFileIcon()}
                      <div>
                        <p className="font-medium">Clique para selecionar ou arraste um arquivo</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          PDF, DOCX, Excel, CSV, JPG, PNG ou WEBP (máx. 10MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {selectedFile && !uploading && (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Arquivo selecionado: <strong>{selectedFile.name}</strong>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Extracted Data Review */}
            {extractedData && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="font-medium">
                      {extractedData.pacientes?.length || 0} paciente(s) encontrado(s)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setExtractedData(null);
                      setSelectedFile(null);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                </div>

                {/* Patient List */}
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {extractedData.pacientes?.map((paciente, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-4 space-y-3 bg-card"
                      >
                        {editingIndex === index ? (
                          // Edit Mode
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Nome</Label>
                                <Input
                                  value={editForm.nome || ''}
                                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">CPF</Label>
                                <Input
                                  value={editForm.cpf || ''}
                                  onChange={(e) => setEditForm({ ...editForm, cpf: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Data Nascimento</Label>
                                <Input
                                  value={editForm.data_nascimento || ''}
                                  onChange={(e) => setEditForm({ ...editForm, data_nascimento: e.target.value })}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Telefone</Label>
                                <Input
                                  value={editForm.telefone || ''}
                                  onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })}
                                />
                              </div>
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Endereço</Label>
                                <Input
                                  value={editForm.endereco || ''}
                                  onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })}
                                />
                              </div>
                              <div className="col-span-2 space-y-1">
                                <Label className="text-xs">Diagnóstico</Label>
                                <Input
                                  value={editForm.diagnostico || ''}
                                  onChange={(e) => setEditForm({ ...editForm, diagnostico: e.target.value })}
                                />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex(null)}
                              >
                                Cancelar
                              </Button>
                              <Button size="sm" onClick={handleSaveEdit}>
                                Salvar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span className="font-semibold">{paciente.nome || 'Nome não encontrado'}</span>
                                <Badge className={`text-xs ${getConfidenceColor(paciente.confianca || 0)}`}>
                                  {Math.round((paciente.confianca || 0) * 100)}% confiança
                                </Badge>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleEditPatient(index)}
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive"
                                  onClick={() => handleRemovePatient(index)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {paciente.cpf && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <span className="font-medium">CPF:</span> {paciente.cpf}
                                </div>
                              )}
                              {paciente.data_nascimento && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <span className="font-medium">Nasc:</span> {paciente.data_nascimento}
                                </div>
                              )}
                              {paciente.telefone && (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <Phone className="h-3 w-3" /> {paciente.telefone}
                                </div>
                              )}
                              {paciente.endereco && (
                                <div className="flex items-center gap-1 text-muted-foreground col-span-2">
                                  <MapPin className="h-3 w-3" /> {paciente.endereco}
                                </div>
                              )}
                              {paciente.diagnostico && (
                                <div className="flex items-center gap-1 text-muted-foreground col-span-2">
                                  <Stethoscope className="h-3 w-3" /> {paciente.diagnostico}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {/* Warning for low confidence */}
                {extractedData.pacientes?.some(p => (p.confianca || 0) < 0.5) && (
                  <Alert variant="warning" className="border-yellow-500 bg-yellow-50">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-700">
                      Alguns dados têm baixa confiança. Revise antes de confirmar.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setModalOpen(false);
                setExtractedData(null);
                setSelectedFile(null);
              }}
            >
              Cancelar
            </Button>
            
            {extractedData?.pacientes?.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Edit before confirm - just keep modal open
                    toast.info('Clique no ícone de lápis para editar cada paciente');
                  }}
                >
                  Editar antes de cadastrar
                </Button>
                <Button
                  onClick={handleConfirmAll}
                  disabled={confirming}
                  className="gap-2"
                >
                  {confirming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Cadastrar Tudo ({extractedData.pacientes.length})
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportarComIA;
